import React, { useEffect, useState, useCallback } from 'react'

import { AutoCompleteProps } from './AutoComplete.types'
import debounce from 'debounce'
import get from 'lodash/get'
import InputWrapper from '../InputWrapper/InputWrapper'
import useInput from '../useInput'
import WithPopup from '../withPopup/withPopup'
import InputList from '../InputList/InputList'
import Icon from '../../Icon/Icon'

const AutoComplete: React.FC<AutoCompleteProps> = (props) => {
    const {
        onClear,
        onInternalChange,
        textField,
        dataItemTemplate,
        placeholder,
        noDataMessage,
        formatQuery,
        suppressPopup = false,
        onQuery,
    } = props
    const [internalVal, setInternalVal] = useState('')
    const [internalDirty, setInternalDirty] = useState(false)
    const [typeDirty, setTypeDirty] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')
    const context = useInput({ ...props, searchQuery, runQuery: internalVal !== '' })
    const { onFocus, onBlur, isLoadingData } = context
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        setLoading(isLoadingData)
    }, [isLoadingData])

    const handleChange = useCallback(
        debounce((value) => {
            let v = value
            if (formatQuery) {
                v = formatQuery(v)
            }
            setSearchQuery(v)
            setTypeDirty(true)
        }, 200),
        [formatQuery],
    )

    const handleInternalChange = (e) => {
        setInternalVal(e.target.value)
        // context.onChange(null)
        if (onInternalChange) {
            onInternalChange(e.target.value)
        }
        // setInternalDirty(true)
    }

    const handleSelect = (selectedDataItem) => {
        const textVal = textField ? get(selectedDataItem, textField) : selectedDataItem
        setInternalVal(textVal)
        setTypeDirty(false)
        context.onChange(context.getValueFromDataItem(selectedDataItem))
    }

    const handleClear = () => {
        if (onClear) {
            onClear()
        }
        context.onChange(null)
        setInternalVal('')
    }

    useEffect(() => {
        if (typeDirty && searchQuery && onQuery) {
            new Promise((resolve) => {
                setLoading(true)
                onQuery(searchQuery, resolve)
            }).then(() => {
                setLoading(false)
            })
        }
    }, [searchQuery])
    useEffect(() => {
        handleChange(internalVal)
        if (internalVal !== '' && !internalDirty) {
            setInternalDirty(true)
        }
    }, [internalVal])

    useEffect(() => {
        if (context.value) {
            const val = textField ? get(context.value, textField) : context.value
            setInternalVal(val)
        }
    }, [context.value])
    const hasValue = context.value !== '' && context.value !== undefined && context.value !== null
    return (
        <InputWrapper {...context}>
            <WithPopup
                onShow={() => {
                    onFocus()
                }}
                onClose={() => {
                    onBlur()
                }}
                closeOnClickInside
                content={({ openPopup }) => (
                    <InputList
                        closeOnItemClick
                        setPopupShow={openPopup}
                        noDataMessage={noDataMessage}
                        isDirty={internalDirty}
                        data={context.data}
                        value={context.value}
                        textField={props.textField}
                        valueField={props.valueField}
                        onChange={handleSelect}
                        isLoading={context.isLoadingData}
                        isFetching={context.isFetchingData}
                        dataItemTemplate={dataItemTemplate}
                    />
                )}
            >
                {(popupContext) => (
                    <div className={`r-autocomplete-container ${loading ? 'r-state-loading' : ''}`}>
                        <div className='r-input-loading-container'>
                            {loading ? (
                                <Icon type={'spinner'} spin />
                            ) : hasValue ? (
                                <Icon type={'check-circle'} className={'r-color-green'} />
                            ) : (
                                <Icon type={'search'} />
                            )}
                        </div>
                        <input
                            onBlur={(e) => {
                                context.onBlur()
                            }}
                            onFocus={(e) => {
                                e.stopPropagation()
                                e.preventDefault()
                                if (!context.isDisabled) {
                                    context.onFocus()
                                    if (!suppressPopup) {
                                        popupContext.openPopup()
                                    }
                                }
                            }}
                            placeholder={placeholder || ''}
                            ref={popupContext.anchor}
                            data-testid='AutoComplete'
                            className='r-input-base'
                            value={internalVal}
                            onChange={handleInternalChange}
                        />
                        {internalVal !== '' && !popupContext.show ? (
                            <button className={'r-clear-button'} onClick={handleClear}>
                                <Icon type={'trash'} size={'xs'} />
                            </button>
                        ) : null}
                    </div>
                )}
            </WithPopup>
        </InputWrapper>
    )
}

export default AutoComplete
